import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Users,
  Star,
  Stethoscope,
  Loader2,
  Wallet,
} from "lucide-react";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageTransition } from "@/components/common/PageTransition";
import { Skeleton } from "@/components/common/Skeletons";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePageHeading } from "@/components/layout/AppLayout";
import { clinicApi, dashboardApi } from "@/lib/api";
import { formatCurrency, formatDate, formatNumber } from "@/lib/format";
import { TID } from "@/constants/testIds";

export default function ClinicDetail() {
  usePageHeading("Clinic Detail", "");
  const { id } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [verificationDocument, setVerificationDocument] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const results = await Promise.allSettled([clinicApi.detail(id), clinicApi.history(id)]);
        const detailRes = results[0];
        const historyRes = results[1];

        if (detailRes.status !== "fulfilled") {
          const err = detailRes.reason;
          const message = err?.response?.data?.message || "Failed to load clinic";
          toast.error(message);
          if (message && message.toLowerCase().includes("clinic not found")) {
            navigate("/approvals");
            return;
          }
          return;
        }

        const c = detailRes.value;
        const h = historyRes.status === "fulfilled" ? historyRes.value : [];
        const rawClinic = c?.clinic ? c.clinic : c;
        const normalizedClinic = {
          ...rawClinic,
          id: rawClinic?.id ?? id,
          name: rawClinic?.clinicName ?? rawClinic?.name ?? "Clinic",
          owner: rawClinic?.doctorName ?? rawClinic?.owner ?? rawClinic?.contactPerson ?? "Owner",
          email: rawClinic?.email ?? "",
          phone: rawClinic?.contactNumber ?? rawClinic?.clinic?.contactNumber ?? rawClinic?.phone ?? "",
          city: rawClinic?.city ?? rawClinic?.clinic?.city ?? "",
          state: rawClinic?.stateName ?? rawClinic?.state ?? "",
          address: rawClinic?.address ?? rawClinic?.clinic?.address ?? "",
          pincode: rawClinic?.pincode ?? rawClinic?.zipCode ?? rawClinic?.clinic?.pincode ?? rawClinic?.clinic?.zipCode ?? "",
          doctorRegistrationNumber: rawClinic?.doctorRegistrationNumber ?? rawClinic?.doctor?.registrationNumber ?? "",
          subscriptionStatus: c?.subscription?.status ?? rawClinic?.subscriptionStatus ?? rawClinic?.status ?? "PENDING",
          status: (c?.subscription?.status ?? rawClinic?.subscriptionStatus ?? rawClinic?.status ?? "PENDING").toLowerCase(),
          mrr: rawClinic?.mrr ?? 0,
          patients: rawClinic?.patients ?? 0,
          staff: rawClinic?.staff ?? 0,
          rating: rawClinic?.rating ?? 0,
          image: rawClinic?.image ?? null,
        };
        setClinic(normalizedClinic);
        setVerificationDocument(c?.verificationDocument ?? null);
        setDoctors(c?.doctors ?? []);
        setSubscription(c?.subscription ?? null);
        setStatus(normalizedClinic.status);
        setHistory((h || []).map((item, index) => ({
          ...item,
          id: item.id ?? `${item.type ?? "event"}-${index}`,
          event: item.type ?? item.event ?? "subscription",
          plan: item.planName ?? item.plan ?? "Trial",
          date: item.createdAt ?? item.startDate ?? item.endDate ?? item.date,
          amount: item.amount ?? 0,
          status: (item.status ?? "active").toLowerCase(),
        })));
      } catch (err) {
        // Unexpected error: show a generic message and return to approvals
        const message = err?.message || "Failed to load clinic";
        toast.error(message);
        navigate("/approvals");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await clinicApi.updateStatus(id, status);
      setClinic((c) => ({ ...c, status }));
      toast.success("Status updated", { description: `${clinic.name} is now ${status}.` });
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const approveClinic = async () => {
    setApproveLoading(true);
    try {
      await dashboardApi.approve(id);
      setClinic((c) => ({ ...c, status: "active" }));
      setStatus("active");
      toast.success("Clinic approved", { description: `${clinic.name} has been approved.` });
    } catch {
      toast.error("Approval failed");
    } finally {
      setApproveLoading(false);
    }
  };

  if (loading) {
    return (
      <PageTransition className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-56 w-full rounded-2xl" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-64 rounded-2xl lg:col-span-2" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </PageTransition>
    );
  }

  if (!clinic) {
    return (
      <PageTransition>
        <Button variant="ghost" onClick={() => navigate("/clinics")} className="mb-4">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <p className="text-muted-foreground">Clinic not found.</p>
      </PageTransition>
    );
  }

  const stats = [
    { label: "Monthly Revenue", value: formatCurrency(clinic.mrr), icon: Wallet },
    { label: "Patients", value: formatNumber(clinic.patients), icon: Users },
    { label: "Staff", value: clinic.staff, icon: Stethoscope },
    { label: "Rating", value: clinic.rating, icon: Star },
  ];

  return (
    <PageTransition className="space-y-6">
      <div data-testid={TID.clinicDetail} />
      <Button variant="ghost" onClick={() => navigate("/clinics")} className="-ml-2 text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-1 h-4 w-4" /> Back to clinics
      </Button>

      {/* Hero */}
      <GlassCard className="overflow-hidden">
        <div className="relative h-40 sm:h-52">
          {clinic.image ? (
            <img src={clinic.image} alt={clinic.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gold/20 via-background to-secondary/50">
              <span className="font-heading text-4xl font-semibold text-gold">{String(clinic.name).slice(0, 2).toUpperCase()}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 flex flex-wrap items-end justify-between gap-3 p-6">
            <div>
              <StatusBadge status={clinic.status} />
              <h2 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-white">{clinic.name}</h2>
              <p className="text-sm text-white/70">{clinic.owner}</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-border/60 p-5 sm:grid-cols-3 sm:gap-6">
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Mail className="h-4 w-4 text-gold" /> {clinic.email}</span>
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Phone className="h-4 w-4 text-gold" /> {clinic.phone}</span>
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><MapPin className="h-4 w-4 text-gold" /> {clinic.city}</span>
        </div>
      </GlassCard>

      <div className="grid gap-6 lg:grid-cols-3">
        <GlassCard className="lg:col-span-2 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-lg font-semibold tracking-tight">Submission details</h3>
              <p className="text-sm text-muted-foreground">Review the clinic signup data and uploaded documents before approving.</p>
            </div>
            <Button
              data-testid={TID.approveClinic}
              onClick={approveClinic}
              disabled={approveLoading || clinic.status === "active"}
              className="h-11 rounded-full bg-lux-emerald text-[#0a2417] hover:bg-lux-emeraldHover"
            >
              {approveLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Approve clinic"}
            </Button>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">Clinic info</h4>
                <div className="mt-3 space-y-2 text-sm text-foreground">
                  <p><span className="font-semibold">State:</span> {clinic.state || "—"}</p>
                  <p><span className="font-semibold">City:</span> {clinic.city || "—"}</p>
                  <p><span className="font-semibold">Pincode:</span> {clinic.pincode || "—"}</p>
                  <p><span className="font-semibold">Address:</span> {clinic.address || "—"}</p>
                  <p><span className="font-semibold">Registration status:</span> {clinic.subscriptionStatus || clinic.status || "Pending"}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">Doctor info</h4>
                <div className="mt-3 space-y-2 text-sm text-foreground">
                  <p><span className="font-semibold">Name:</span> {clinic.owner || "—"}</p>
                  <p><span className="font-semibold">Email:</span> {clinic.email || "—"}</p>
                  <p><span className="font-semibold">Phone:</span> {clinic.phone || "—"}</p>
                  <p><span className="font-semibold">Registration no.:</span> {clinic.doctorRegistrationNumber || "—"}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">Assigned doctors</h4>
              <div className="mt-3 space-y-3">
                {doctors.length ? doctors.map((doc) => (
                  <div key={doc.id} className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
                    <p className="font-semibold">{doc.doctorName || "Doctor"}</p>
                    <p className="text-xs text-muted-foreground">{doc.specialization || "General"}</p>
                    <p className="text-xs text-muted-foreground">Reg. No.: {doc.registrationNumber || "—"}</p>
                  </div>
                )) : (
                  <p className="text-sm text-muted-foreground">No additional doctor details available.</p>
                )}
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="font-heading text-lg font-semibold tracking-tight">Submitted documents</h3>
          <p className="text-sm text-muted-foreground">Open each link to verify the uploaded files.</p>

          <div className="mt-6 space-y-4">
            <div className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
              <p className="text-sm font-semibold">Doctor registration certificate</p>
              {verificationDocument?.doctorRegistrationCertificateUrl ? (
                <a href={verificationDocument.doctorRegistrationCertificateUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-lux-emerald hover:underline">
                  View document
                </a>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">Not provided</p>
              )}
            </div>

            <div className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
              <p className="text-sm font-semibold">Clinic registration certificate</p>
              {verificationDocument?.clinicRegistrationCertificateUrl ? (
                <a href={verificationDocument.clinicRegistrationCertificateUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-lux-emerald hover:underline">
                  View document
                </a>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">Not provided</p>
              )}
            </div>

            <div className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
              <p className="text-sm font-semibold">GST certificate</p>
              {verificationDocument?.gstCertificateUrl ? (
                <a href={verificationDocument.gstCertificateUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-lux-emerald hover:underline">
                  View document
                </a>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">Not provided</p>
              )}
            </div>

            <div className="rounded-2xl border border-border/60 bg-secondary/40 p-4">
              <p className="text-sm font-semibold">Clinic photos</p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  verificationDocument?.clinicPhoto1Url,
                  verificationDocument?.clinicPhoto2Url,
                  verificationDocument?.clinicPhoto3Url,
                ].map((photoUrl, idx) => (
                  <div key={idx} className="overflow-hidden rounded-2xl border border-border/60 bg-card">
                    {photoUrl ? (
                      <a href={photoUrl} target="_blank" rel="noreferrer">
                        <img src={photoUrl} alt={`Clinic photo ${idx + 1}`} className="h-28 w-full object-cover" />
                      </a>
                    ) : (
                      <div className="flex h-28 items-center justify-center text-sm text-muted-foreground">Photo {idx + 1} missing</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <GlassCard className="p-5" hover>
              <s.icon className="h-5 w-5 text-lux-emerald" />
              <p className="mt-3 font-heading text-2xl font-semibold tracking-tight">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* History */}
        <GlassCard className="p-6 lg:col-span-2">
          <h3 className="mb-5 font-heading text-lg font-semibold tracking-tight">Subscription History</h3>
          <div className="space-y-3">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between rounded-xl border border-border/50 bg-secondary/20 p-4">
                <div>
                  <p className="text-sm font-semibold capitalize">{h.event.replace(/_/g, " ")}</p>
                  <p className="text-xs text-muted-foreground">{h.plan} · {formatDate(h.date)}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gold">{formatCurrency(h.amount)}</p>
                  <StatusBadge status={h.status} />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Status update */}
        <GlassCard className="h-fit p-6">
          <h3 className="mb-2 font-heading text-lg font-semibold tracking-tight">Update Status</h3>
          <p className="mb-5 text-sm text-muted-foreground">Change the operational status of this clinic.</p>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 border-border bg-secondary/30">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="hold">On Hold</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid={TID.updateStatus}
            onClick={save}
            disabled={saving || status === clinic.status}
            className="mt-4 h-11 w-full rounded-full bg-lux-emerald text-[#0a2417] hover:bg-lux-emeraldHover"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}
          </Button>
        </GlassCard>
      </div>
    </PageTransition>
  );
}
